import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, Calendar, MapPin, Loader2, ScrollText, Search,
  Lock, AlertTriangle, CheckCircle2, Wallet, ArrowLeftRight, X,
} from 'lucide-react';
import { PRODUCTOS } from '../../constants/productos';
import { fmtMoney } from '../../utils/money';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOMBRE_PRODUCTO = Object.fromEntries(PRODUCTOS.map((p) => [String(p.clave), p.nombre]));

function isoToday() {
  return new Date().toISOString().split('T')[0];
}

const CATEGORIAS = [
  { key: 'todos',     label: 'Todo' },
  { key: 'producto',  label: 'Producto' },
  { key: 'dinero',    label: 'Dinero' },
  { key: 'turno',     label: 'Turno' },
  { key: 'precio',    label: 'Precios' },
  { key: 'rechazado', label: 'Rechazadas' },
];

const CAT_STYLE = {
  producto: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  dinero:   { dot: 'bg-blue-500',    chip: 'bg-blue-50 text-blue-700 border-blue-200' },
  turno:    { dot: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  precio:   { dot: 'bg-zinc-400',    chip: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
};

function fmtHora(ts) {
  if (!ts) return '--:--';
  const d = new Date(ts);
  if (isNaN(d)) return '--:--';
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function ProductosChips({ productos }) {
  if (!productos || Object.keys(productos).length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {Object.entries(productos).map(([clave, cant]) => (
        <span
          key={clave}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-zinc-100 border border-zinc-200 text-[11px] font-medium text-zinc-600"
        >
          {NOMBRE_PRODUCTO[String(clave)] || `#${clave}`}
          <span className="font-mono text-zinc-400">×{cant}</span>
        </span>
      ))}
    </div>
  );
}

const TOCA_EFECTIVO = new Set([
  'venta', 'abono', 'entrada_dinero', 'salida_dinero', 'gasto', 'gasto_eliminado', 'corte',
]);

function EventoRow({ ev, showSaldos, onOpenTraslado }) {
  const style = CAT_STYLE[ev.categoria] || CAT_STYLE.precio;
  const dif = ev.tipo === 'corte' ? Number(ev.meta?.diferencia || 0) : null;
  const rechazado = ev.resultado === 'rechazado';
  const canal = ev.meta?.canal;
  const esTraslado = !!ev.correlation_id && !rechazado;
  const mostrarSaldoEfectivo = showSaldos && !rechazado && TOCA_EFECTIVO.has(ev.tipo) && ev.saldo_efectivo != null;

  return (
    <div
      className={`flex gap-3 px-4 py-3 ${rechazado ? 'bg-red-50/40' : ''} ${esTraslado ? 'cursor-pointer hover:bg-zinc-50' : ''}`}
      data-testid="bitacora-evento"
      onClick={esTraslado ? () => onOpenTraslado(ev.correlation_id) : undefined}
    >
      {/* Secuencia + línea */}
      <div className="flex flex-col items-center pt-0.5">
        <span className="text-[11px] font-mono text-zinc-400 w-6 text-right">{ev.seq}</span>
        <span className={`w-2.5 h-2.5 rounded-full mt-1 ${rechazado ? 'bg-red-500' : style.dot}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className={`text-sm font-semibold ${rechazado ? 'text-red-700' : 'text-zinc-900'}`}>{ev.titulo}</span>
            {esTraslado && <ArrowLeftRight className="inline w-3.5 h-3.5 ml-1 text-zinc-400" />}
            <span className="ml-2 text-xs text-zinc-400 font-mono">{fmtHora(ev.ts)}</span>
          </div>
          <div className="shrink-0 text-right">
            {ev.monto != null && (
              <span className="text-sm font-semibold text-zinc-800 font-mono block">
                {fmtMoney(ev.monto)}
              </span>
            )}
            {mostrarSaldoEfectivo && (
              <span className="text-[11px] text-zinc-400 font-mono block">
                caja: {fmtMoney(ev.saldo_efectivo)}
              </span>
            )}
          </div>
        </div>

        {(rechazado || canal === 'control_remoto' || canal === 'sync_offline') && (
          <div className="flex flex-wrap gap-1 mt-1">
            {rechazado && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-red-200 bg-red-100 text-red-700 text-[11px] font-semibold">
                <AlertTriangle className="w-3 h-3" /> Rechazado
              </span>
            )}
            {canal === 'control_remoto' && (
              <span className="px-1.5 py-0.5 rounded-md border border-amber-200 bg-amber-50 text-amber-700 text-[11px] font-medium">
                Control remoto
              </span>
            )}
            {canal === 'sync_offline' && (
              <span className="px-1.5 py-0.5 rounded-md border border-sky-200 bg-sky-50 text-sky-700 text-[11px] font-medium">
                Sincronizado offline
              </span>
            )}
          </div>
        )}

        {ev.detalle && (
          <p className={`text-xs mt-0.5 break-words ${rechazado ? 'text-red-600' : 'text-zinc-500'}`}>{ev.detalle}</p>
        )}

        {dif != null && dif !== 0 && (
          <span className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md border text-[11px] font-semibold ${
            dif < 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}>
            <AlertTriangle className="w-3 h-3" />
            {dif < 0 ? 'Faltante ' : 'Sobrante '}{fmtMoney(Math.abs(dif))}
          </span>
        )}

        {ev.meta?.faltante && (
          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md border border-red-200 bg-red-50 text-red-700 text-[11px] font-semibold">
            <AlertTriangle className="w-3 h-3" />
            Diferencia al confirmar
          </span>
        )}

        <ProductosChips productos={ev.productos} />

        {showSaldos && !rechazado && ev.saldo_inventario && Object.keys(ev.saldo_inventario).length > 0 && (
          <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 text-[11px] text-zinc-400 font-mono">
            <span className="text-zinc-500 font-semibold">saldo:</span>
            {Object.entries(ev.saldo_inventario)
              .filter(([, v]) => Math.abs(v) > 0.001)
              .map(([clave, v]) => (
                <span key={clave} className={v < 0 ? 'text-red-500 font-semibold' : ''}>
                  {(NOMBRE_PRODUCTO[String(clave)] || `#${clave}`).slice(0, 8)}&nbsp;{v}
                </span>
              ))}
          </div>
        )}

        {ev.actor && (
          <div className="mt-1.5">
            <span className={`inline-block px-1.5 py-0.5 rounded-md border text-[11px] font-medium ${style.chip}`}>
              {ev.actor}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function ProdList({ data, faltante }) {
  if (data == null) return <span className="text-zinc-400">—</span>;
  if (typeof data === 'number') return <span className="font-mono">{fmtMoney(data)}</span>;
  const keys = Object.keys(data);
  if (keys.length === 0) return <span className="text-zinc-400">—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {keys.map((k) => {
        const falt = faltante && faltante[String(k)];
        return (
          <div key={k} className="flex justify-between gap-3 text-xs">
            <span className="text-zinc-600">{NOMBRE_PRODUCTO[String(k)] || `#${k}`}</span>
            <span className="font-mono text-zinc-800">
              {data[k]}
              {falt ? <span className="text-red-600 font-semibold"> (−{falt})</span> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TrasladoModal({ correlationId, onClose }) {
  const [t, setT] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bitacora/traslado/${encodeURIComponent(correlationId)}`);
        if (alive) setT(data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'No se pudo cargar el traslado');
      }
    })();
    return () => { alive = false; };
  }, [correlationId]);

  const esDinero = t?.tipo === 'dinero';
  const faltanteMonto = esDinero && typeof t?.faltante === 'number' ? t.faltante : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-zinc-500" />
            <h3 className="font-bold text-zinc-900">
              {esDinero ? 'Traslado de dinero' : 'Traslado de producto'}
            </h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {err && <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{err}</div>}
        {!t && !err && (
          <div className="flex items-center justify-center gap-2 py-16 text-zinc-400 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando...
          </div>
        )}

        {t && (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 text-xs">
              <span className={`px-2 py-0.5 rounded-md border font-semibold ${
                t.status === 'recibido' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : t.status === 'cancelado' ? 'bg-zinc-100 text-zinc-500 border-zinc-200'
                : t.status === 'rechazado' ? 'bg-red-50 text-red-700 border-red-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {t.status === 'recibido' ? 'Confirmado' : t.status === 'cancelado' ? 'Cancelado'
                  : t.status === 'rechazado' ? 'Rechazado por destino' : 'En tránsito'}
              </span>
              {t.es_sobrante_corte && (
                <span className="px-2 py-0.5 rounded-md border border-zinc-200 bg-zinc-50 text-zinc-500">Sobrante de corte</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-zinc-200 p-3">
                <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold">Origen</p>
                <p className="text-sm font-bold text-zinc-900">{t.origen.punto_nombre}</p>
                <p className="text-[11px] text-zinc-400">{t.origen.fecha_turno || '—'}</p>
                <p className="text-xs text-zinc-500 mt-1">Envió: {t.creado_por || '—'}</p>
              </div>
              <div className="rounded-xl border border-zinc-200 p-3">
                <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold">Destino</p>
                <p className="text-sm font-bold text-zinc-900">{t.destino.punto_nombre}</p>
                <p className="text-[11px] text-zinc-400">{t.destino.fecha_turno || '—'}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {t.status === 'cancelado' ? `Canceló: ${t.cancelado_por || '—'}`
                    : t.status === 'rechazado' ? `Rechazó: ${t.rechazado_por || '—'}`
                    : `Confirmó: ${t.confirmado_por || '—'}`}
                </p>
                {t.status === 'rechazado' && t.motivo_rechazo && (
                  <p className="text-xs text-red-600 mt-1">Motivo: {t.motivo_rechazo}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">Enviado</p>
                <ProdList data={t.enviado} />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold mb-1">Confirmado</p>
                <ProdList data={t.confirmado} faltante={!esDinero ? t.faltante : null} />
              </div>
            </div>

            {(t.faltante || faltanteMonto) && (
              <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {esDinero
                  ? `Faltante: ${fmtMoney(faltanteMonto)}`
                  : `Faltó producto al confirmar: ${Object.entries(t.faltante).map(([k, v]) => `${NOMBRE_PRODUCTO[String(k)] || k} −${v}`).join(', ')}`}
              </div>
            )}

            {t.nota && <p className="text-xs text-zinc-500">Nota: {t.nota}</p>}

            {t.eventos?.length > 0 && (
              <div className="pt-2 border-t border-zinc-100">
                <p className="text-[11px] uppercase tracking-wide text-zinc-400 font-semibold mb-1.5">Secuencia registrada</p>
                <div className="space-y-1">
                  {t.eventos.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-400 font-mono">{fmtHora(e.ts)}</span>
                      <span className={e.resultado === 'rechazado' ? 'text-red-600' : 'text-zinc-700'}>{e.titulo}</span>
                      {e.canal && e.canal !== 'pos' && (
                        <span className="text-[10px] text-zinc-400">({e.canal.replace('_', ' ')})</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminBitacora() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [puntos, setPuntos] = useState([]);
  const [selectedPunto, setSelectedPunto] = useState(searchParams.get('punto') || '');
  const [fecha, setFecha] = useState(searchParams.get('fecha') || isoToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cat, setCat] = useState('todos');
  const [q, setQ] = useState('');
  const [showSaldos, setShowSaldos] = useState(false);
  const [trasladoAbierto, setTrasladoAbierto] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: pv } = await axios.get(`${API}/puntos-venta`);
        const sorted = pv.sort((a, b) => a.name.localeCompare(b.name, 'es'));
        setPuntos(sorted);
        const fromUrl = searchParams.get('punto');
        if (fromUrl && sorted.some((p) => p.id === fromUrl)) return; // ya seteado
        const inicial = user?.punto_venta_id && sorted.some((p) => p.id === user.punto_venta_id)
          ? user.punto_venta_id
          : (sorted[0]?.id || '');
        setSelectedPunto(inicial);
      } catch {
        setError('No se pudieron cargar los puntos de venta');
      }
    })();
  }, [user?.punto_venta_id]);

  const fetchBitacora = useCallback(async () => {
    if (!selectedPunto) return;
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(
        `${API}/bitacora/${selectedPunto}?fecha=${fecha}`
      );
      setData(res);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.detail || 'No se pudo cargar la bitácora');
    } finally {
      setLoading(false);
    }
  }, [selectedPunto, fecha]);

  useEffect(() => { fetchBitacora(); }, [fetchBitacora]);

  const eventosFiltrados = useMemo(() => {
    if (!data?.eventos) return [];
    const texto = q.trim().toLowerCase();
    return data.eventos.filter((ev) => {
      if (cat === 'rechazado') {
        if (ev.resultado !== 'rechazado') return false;
      } else if (cat !== 'todos' && ev.categoria !== cat) {
        return false;
      }
      if (!texto) return true;
      return (
        ev.titulo?.toLowerCase().includes(texto) ||
        ev.detalle?.toLowerCase().includes(texto) ||
        ev.actor?.toLowerCase().includes(texto)
      );
    });
  }, [data, cat, q]);

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" data-testid="admin-bitacora-page">
      {/* Header */}
      <div className="bg-white px-4 py-3 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center border border-zinc-300 rounded-lg"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <ScrollText className="w-5 h-5 text-zinc-500" />
            <h1 className="text-xl font-bold text-zinc-900">Bitácora</h1>
          </div>
        </div>
      </div>

      <div className="flex-1 px-4 py-5 space-y-4 pb-12">

        {/* Controles */}
        <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3">
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-600 mb-1.5">
              <MapPin className="w-4 h-4" /> Punto de venta
            </label>
            <select
              value={selectedPunto}
              onChange={(e) => setSelectedPunto(e.target.value)}
              className="w-full h-12 px-3 bg-white border border-zinc-300 rounded-xl text-base font-medium text-zinc-900"
              data-testid="select-punto"
            >
              {puntos.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-600 mb-1.5">
              <Calendar className="w-4 h-4" /> Fecha del turno
            </label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full h-12 px-3 bg-white border border-zinc-300 rounded-xl text-base font-medium text-zinc-900"
              data-testid="input-fecha"
            />
          </div>

          <div className="relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar en la bitácora..."
              className="w-full h-11 pl-9 pr-3 bg-white border border-zinc-300 rounded-xl text-sm text-zinc-900"
              data-testid="input-buscar"
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {CATEGORIAS.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  cat === c.key
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-600 border-zinc-300'
                }`}
              >
                {c.label}
              </button>
            ))}
            <button
              onClick={() => setShowSaldos((v) => !v)}
              className={`ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                showSaldos ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-zinc-600 border-zinc-300'
              }`}
            >
              <Wallet className="w-3.5 h-3.5" /> Saldos
            </button>
          </div>
        </div>

        {/* Resumen */}
        {data && !loading && (
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="px-2.5 py-1 rounded-lg bg-white border border-zinc-200 text-zinc-600 font-medium">
              {eventosFiltrados.length} de {data.total} operaciones
            </span>
            {data.rechazados > 0 && (
              <button
                onClick={() => setCat('rechazado')}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-50 border border-red-200 text-red-700 font-semibold"
              >
                <AlertTriangle className="w-3.5 h-3.5" /> {data.rechazados} rechazada{data.rechazados === 1 ? '' : 's'}
              </button>
            )}
            {data.cerrado ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-zinc-100 border border-zinc-200 text-zinc-600 font-medium">
                <Lock className="w-3.5 h-3.5" /> Turno cerrado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" /> Turno abierto
              </span>
            )}
            {data.cerrado && Number(data.diferencia_corte || 0) !== 0 && (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border font-semibold ${
                Number(data.diferencia_corte) < 0
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                <AlertTriangle className="w-3.5 h-3.5" />
                Corte: {Number(data.diferencia_corte) < 0 ? 'faltan ' : 'sobran '}
                {fmtMoney(Math.abs(Number(data.diferencia_corte)))}
              </span>
            )}
          </div>
        )}

        {/* Saldos reconstruidos */}
        {showSaldos && data && !loading && data.saldos && (
          <div className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Efectivo teórico (reconstruido evento por evento)</span>
              <span className="font-mono font-semibold text-zinc-900">{fmtMoney(data.saldos.efectivo_teorico)}</span>
            </div>
            {data.saldos.corte && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Esperado según corte</span>
                  <span className="font-mono text-zinc-700">{fmtMoney(data.saldos.corte.esperado)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500">Efectivo contado</span>
                  <span className="font-mono text-zinc-700">{fmtMoney(data.saldos.corte.efectivo_contado)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-zinc-100 pt-2">
                  <span className="text-zinc-600 font-medium">Diferencia del corte</span>
                  <span className={`font-mono font-bold ${Number(data.saldos.corte.diferencia) < 0 ? 'text-red-600' : Number(data.saldos.corte.diferencia) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    {fmtMoney(data.saldos.corte.diferencia)}
                  </span>
                </div>
                {Math.abs(Number(data.saldos.efectivo_teorico) - Number(data.saldos.corte.esperado)) > 0.5 && (
                  <p className="text-[11px] text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    El teórico de la bitácora no coincide con el esperado del corte — revisa operaciones fuera de secuencia.
                  </p>
                )}
              </>
            )}
            {Object.keys(data.saldos.inventario || {}).some((k) => data.saldos.inventario[k] < -0.001) && (
              <p className="text-[11px] text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Hay productos con saldo negativo al cierre — se vendió/salió más de lo disponible.
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Línea de tiempo */}
        <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-zinc-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin" /> Cargando bitácora...
            </div>
          ) : eventosFiltrados.length === 0 ? (
            <div className="py-16 text-center text-zinc-400 text-sm">
              Sin operaciones para mostrar
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {eventosFiltrados.map((ev) => (
                <EventoRow
                  key={`${ev.seq}-${ev.ref_id || ev.tipo}`}
                  ev={ev}
                  showSaldos={showSaldos}
                  onOpenTraslado={setTrasladoAbierto}
                />
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-zinc-400 px-1">
          Línea de tiempo de las operaciones del turno (ventas, movimientos, tránsitos,
          gastos, abonos, precios y corte) e intentos rechazados, ordenados por hora.
          Toca un tránsito para ver origen y destino lado a lado.
        </p>
      </div>

      {trasladoAbierto && (
        <TrasladoModal correlationId={trasladoAbierto} onClose={() => setTrasladoAbierto(null)} />
      )}
    </div>
  );
}
